import { Add, Remove } from "@mui/icons-material";
import { FormControl, FormHelperText, FormLabel, IconButton, Input, Stack } from "@mui/joy";
import { ReactNode } from "react";
import { useController, useFormContext } from "react-hook-form";
import { NumericFormat } from "react-number-format";

type SteppableNumberInputProps = {
  name: string;
  label: ReactNode | null;
  min?: number;
  max?: number;
  step?: number;
  decimalScale?: number;
  disabled?: boolean;
};

export function SteppableNumberInput({
  name,
  label,
  min,
  max,
  step = 1,
  decimalScale = 0,
  disabled,
}: SteppableNumberInputProps) {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ name, control });

  const value = field.value ?? 0;

  const canDecrement = min === undefined || value > min;
  const canIncrement = max === undefined || value < max;

  const handleDecrement = () => {
    if (canDecrement) {
      const newValue = value - step;
      field.onChange(min !== undefined ? Math.max(min, newValue) : newValue);
    }
  };

  const handleIncrement = () => {
    if (canIncrement) {
      const newValue = value + step;
      field.onChange(max !== undefined ? Math.min(max, newValue) : newValue);
    }
  };

  return (
    <FormControl error={Boolean(fieldState.error)}>
      {label !== null ? <FormLabel>{label}</FormLabel> : null}
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton
          disabled={!canDecrement || disabled}
          onClick={handleDecrement}
          variant="soft"
          sx={{ borderRadius: 100, cursor: "pointer" }}
        >
          <Remove />
        </IconButton>

        <NumericFormat
          customInput={Input}
          allowNegative={min === undefined || min < 0}
          decimalScale={decimalScale}
          value={value}
          onValueChange={(values, sourceInfo) => {
            if (sourceInfo.source === "prop") {
              return;
            }
            if (values.floatValue !== undefined) {
              let newValue = values.floatValue;
              if (min !== undefined) {
                newValue = Math.max(min, newValue);
              }
              if (max !== undefined) {
                newValue = Math.min(max, newValue);
              }
              field.onChange(newValue);
            }
            if (values.floatValue === undefined && values.value === "") {
              field.onChange(min ?? 0);
            }
          }}
          onFocus={(e) => {
            // Select all text on focus
            e.target.select();
          }}
          onMouseUp={(e) => {
            // Prevent mouseup from deselecting text
            e.preventDefault();
          }}
          onBlur={field.onBlur}
          disabled={disabled}
          type="tel"
          sx={{
            flex: 1,
            textAlign: "center",
            input: {
              textAlign: "center",
              fontSize: 20,
              fontWeight: "bold",
            },
          }}
        />

        <IconButton
          disabled={!canIncrement || disabled}
          onClick={handleIncrement}
          variant="soft"
          sx={{ borderRadius: 100, cursor: "pointer" }}
        >
          <Add />
        </IconButton>
      </Stack>
      {fieldState.error?.message ? (
        <FormHelperText>{fieldState.error?.message}</FormHelperText>
      ) : null}
    </FormControl>
  );
}

export default SteppableNumberInput;
